import {
    FormControl,
    FormHelperText,
    FormLabel,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Select,
    SelectProps,
} from '@material-ui/core';
import { useField } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useCommonStyles from '../../useCommonStyles';
import OutlinedInputField, { OutlinedInputFieldProps } from './OutlinedInputField';

type MinMax = { min?: number; max?: number };

export type OutlinedNumericUnitInputFieldProps = Omit<OutlinedInputFieldProps, 'name' | 'endAdornment'> & {
    currencySymbol: string;
    name: string;
    selectName?: string;
    selectProps?: Omit<SelectProps, 'name' | 'value'>;
    formatToFixed?: number;
    minMax?: { percentage?: MinMax; amount?: MinMax };
    onUnitChanged?: (unit: string) => void;
};

const UNIT_TYPES = { amount: 'Amount', percentage: 'Percentage' };

const OutlinedNumericUnitInputField = ({
    currencySymbol,
    name,
    selectProps,
    minMax,
    onUnitChanged,
    ...props
}: OutlinedNumericUnitInputFieldProps) => {
    const commonStyles = useCommonStyles();

    const valueFieldName = `${name}.value`;
    const unitFieldName = `${name}.unit`;

    const [{ value: selectValue, ...selectField }, , selectHelpers] = useField({ name: unitFieldName });
    const [inputField, inputMeta, inputHelpers] = useField({ name: valueFieldName });

    const inputValue = inputField.value;
    const { onBlur: inputOnBlur, value: _inputFieldValue, ...inputFieldRest } = inputField;

    const selectedOption = selectValue;

    const selectDisabled = selectProps?.disabled ?? props.disabled;
    const hasError = !!inputMeta.error && inputMeta.touched;

    const { formatToFixed } = props;
    const shouldFormatNumeric = formatToFixed !== undefined && formatToFixed !== null;

    const valueUnitOptions = [
        { value: UNIT_TYPES.amount, label: currencySymbol },
        { value: UNIT_TYPES.percentage, label: '%' },
    ];

    const formattedFromValue = useMemo(() => {
        if (!shouldFormatNumeric) {
            return inputValue === null ? '' : inputValue;
        }

        if (inputValue === '' || inputValue === null || inputValue === undefined) {
            return '';
        }

        const asNumber = typeof inputValue === 'number' ? inputValue : Number(inputValue);

        if (Number.isNaN(asNumber)) {
            return '';
        }

        return asNumber.toFixed(formatToFixed);
    }, [formatToFixed, inputValue, shouldFormatNumeric]);

    const [displayValue, setDisplayValue] = useState<string>(() => String(formattedFromValue ?? ''));
    const isEditingRef = useRef(false);

    useEffect(() => {
        if (!shouldFormatNumeric) {
            return;
        }

        if (isEditingRef.current) {
            return;
        }

        setDisplayValue(String(formattedFromValue ?? ''));
    }, [formattedFromValue, shouldFormatNumeric]);

    const endAdornment = (
        <InputAdornment position="end">
            <Select
                {...selectProps}
                {...selectField}
                disabled={selectDisabled}
                inputProps={{
                    ...(selectProps?.inputProps || {}),
                    classes: {
                        ...(selectProps?.inputProps as any)?.classes,
                        icon: commonStyles.fillSecondary,
                    },
                }}
                onChange={event => {
                    const nextUnit = (event.target as any).value;
                    selectHelpers.setValue(nextUnit);
                    onUnitChanged?.(nextUnit);
                }}
                value={selectValue === null ? '' : selectValue}
                variant="standard"
                autoWidth
                disableUnderline
            >
                {valueUnitOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </InputAdornment>
    );

    if (!shouldFormatNumeric) {
        return <OutlinedInputField {...props} endAdornment={endAdornment} name={name} />;
    }

    const isNumericDraft = (value: string) => {
        if (value === '') {
            return true;
        }

        return /^\d*\.?\d*$/.test(value);
    };

    const mergedInputProps = {
        ...(props.inputProps || {}),
        inputMode: (props.inputProps as any)?.inputMode ?? 'decimal',
        pattern: (props.inputProps as any)?.pattern ?? '[0-9]*[.,]?[0-9]*',
    };

    return (
        <FormControl fullWidth>
            {props.label && (
                <FormLabel className={commonStyles.formLabel} disabled={props.disabled} required={props.required}>
                    {props.label}
                </FormLabel>
            )}
            <OutlinedInput
                autoComplete="off"
                {...props}
                {...inputFieldRest}
                endAdornment={endAdornment}
                error={hasError}
                inputProps={mergedInputProps}
                labelWidth={0}
                notched={false}
                onBlur={event => {
                    isEditingRef.current = false;
                    inputOnBlur(event);
                    props.onBlur?.(event);

                    const raw = event.currentTarget.value;

                    if (!isNumericDraft(raw)) {
                        inputHelpers.setValue(null);
                        setDisplayValue('');

                        return;
                    }

                    if (raw === '' || raw === null || raw === undefined || raw === '.') {
                        inputHelpers.setValue(null);
                        setDisplayValue('');

                        return;
                    }

                    let asNumber = Number(raw);

                    if (Number.isNaN(asNumber)) {
                        inputHelpers.setValue(null);

                        return;
                    }

                    if (minMax) {
                        if (minMax.percentage && selectedOption === UNIT_TYPES.percentage) {
                            const { min, max } = minMax.percentage;
                            if (min != null && asNumber < min) {
                                asNumber = min;
                            }
                            if (max != null && asNumber > max) {
                                asNumber = max;
                            }
                        }

                        if (minMax.amount && selectedOption === UNIT_TYPES.amount) {
                            const { min, max } = minMax.amount;
                            if (min != null && asNumber < min) {
                                asNumber = min;
                            }
                            if (max != null && asNumber > max) {
                                asNumber = max;
                            }
                        }
                    }

                    inputHelpers.setValue(asNumber);
                    setDisplayValue(asNumber.toFixed(formatToFixed));
                }}
                onChange={event => {
                    const raw = event.currentTarget.value;

                    if (!isNumericDraft(raw)) {
                        return;
                    }

                    setDisplayValue(raw);

                    if (raw === '' || raw === null || raw === undefined || raw === '.') {
                        inputHelpers.setValue(null);

                        return;
                    }

                    const asNumber = Number(raw);
                    inputHelpers.setValue(Number.isNaN(asNumber) ? null : asNumber);
                }}
                onFocus={event => {
                    isEditingRef.current = true;
                    props.onFocus?.(event);
                }}
                type="number"
                value={displayValue}
            />
            <FormHelperText error={hasError}>{hasError ? inputMeta.error : ''}</FormHelperText>
            {props.helperText && props.helperText(inputValue as any)}
        </FormControl>
    );
};

export default OutlinedNumericUnitInputField;
